import { Check, Copy, Download, X } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"

import { KeyboardShortcut } from "@tokenterminal/core/ui/keyboard/KeyboardShortcut"
import {
  HeaderContainer,
  HeaderTitle,
  HeaderTitleIconRow,
  HeaderTitleStack,
  ModalCloseButton,
} from "@tokenterminal/core/ui/overlays/modal/Header"
import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import toast from "modules/account/views/Team/toast"
import { useRef, useEffect, useCallback } from "react"
import {
  useCopyElementRefAsImage,
  useSaveElementRefAsImage,
} from "../CopyAsImage/useCopyAsImage"

type CopyAsImageModalProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  children: (props: { ref: React.RefObject<HTMLDivElement> }) => React.ReactNode
}

/**
 * A generic modal wrapper for copying an element as an image.
 */

export const CopyAsImageModal = ({
  isOpen,
  setIsOpen,
  children,
}: CopyAsImageModalProps) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const copyAsImage = useCopyElementRefAsImage(elementRef)
  const saveAsImage = useSaveElementRefAsImage(elementRef)
  const handleCopyAsImage = useCallback(() => {
    try {
      copyAsImage()
      toast({
        body: "Image copied to clipboard",
        icon: Check,
      })
    } catch (error) {
      console.error("Failed to copy as image:", error)
      toast({
        body: "Failed to copy as image",
        icon: X,
      })
    }
  }, [copyAsImage])
  const handleSaveAsImage = useCallback(() => {
    try {
      saveAsImage()
      toast({
        body: "Image saved",
        icon: Check,
      })
    } catch (error) {
      console.error("Failed to save as image:", error)
      toast({
        body: "Failed to save as image",
        icon: X,
      })
    }
  }, [saveAsImage])

  // Add keyboard shortcuts for copy (Cmd+C) and save (Cmd+S)
  useEffect(() => {
    if (!isOpen) return

    // Detect if we're on Mac to use the correct modifier key
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0

    // Use Command key on Mac, Control key on other platforms
    const modifierKey = isMac ? "metaKey" : "ctrlKey"

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if the modifier key is pressed
      if (e[modifierKey]) {
        if (e.key === "c") {
          e.preventDefault()
          handleCopyAsImage()
        } else if (e.key === "s") {
          e.preventDefault() // Prevent browser's save dialog
          handleSaveAsImage()
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [isOpen, handleCopyAsImage, handleSaveAsImage])

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      size="auto"
      className="rounded-2xl"
      hasDividers
    >
      <HeaderContainer className="px-5 pb-0">
        <HeaderTitleIconRow>
          <HeaderTitleStack>
            <HeaderTitle>Copy as image</HeaderTitle>
          </HeaderTitleStack>
        </HeaderTitleIconRow>
        <div className="flex items-center gap-2">
          <Button onPress={handleSaveAsImage}>
            <Icon as={Download} />
            Save image
          </Button>
          <Tooltip
            content={
              <KeyboardShortcut.Group>
                <KeyboardShortcut>
                  {KeyboardShortcut.CommandSymbol}
                </KeyboardShortcut>
                <KeyboardShortcut>C</KeyboardShortcut>
              </KeyboardShortcut.Group>
            }
          >
            <Button onPress={handleCopyAsImage}>
              <Icon as={Copy} />
              Copy image
            </Button>
          </Tooltip>
          <ModalCloseButton size="medium" className="-m-1" />
        </div>
      </HeaderContainer>
      <Modal.Body className="p-5 flex flex-row items-start gap-5">
        {children({ ref: elementRef })}
      </Modal.Body>
    </Modal>
  )
}
